'use client';

import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Highlighter, Strikethrough, X, Send } from 'lucide-react';
import { SelectionInfo, ViewerAnnotation } from './types';

interface AnnotationToolbarProps {
  selection: SelectionInfo | null;
  onAddAnnotation: (type: ViewerAnnotation['type'], comment?: string) => void;
  onClose: () => void;
}

export function AnnotationToolbar({ selection, onAddAnnotation, onClose }: AnnotationToolbarProps) {
  const [mode, setMode] = useState<'buttons' | 'comment'>('buttons');
  const [comment, setComment] = useState('');
  const toolbarRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset mode when selection changes
  useEffect(() => {
    setMode('buttons');
    setComment('');
  }, [selection]);

  // Focus textarea when entering comment mode
  useEffect(() => {
    if (mode === 'comment' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [mode]);

  // Close toolbar when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!selection) return null;

  // Calculate position (above the selection)
  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.max(10, selection.rect.left + selection.rect.width / 2 - (mode === 'comment' ? 150 : 70)),
    top: Math.max(10, selection.rect.top - (mode === 'comment' ? 120 : 50)),
    zIndex: 50,
  };

  const handleComment = () => {
    setMode('comment');
  };

  const handleHighlight = () => {
    onAddAnnotation('highlight');
  };

  const handleDeletion = () => {
    onAddAnnotation('deletion');
  };

  const handleSubmitComment = () => {
    if (comment.trim()) {
      onAddAnnotation('comment', comment.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmitComment();
    }
    if (e.key === 'Escape') {
      if (mode === 'comment') {
        setMode('buttons');
        setComment('');
      } else {
        onClose();
      }
    }
  };

  return (
    <div
      ref={toolbarRef}
      style={style}
      className="animate-in fade-in slide-in-from-bottom-2 duration-200"
      data-annotation-toolbar
    >
      {mode === 'buttons' ? (
        <div className="flex items-center gap-1 bg-bg-card shadow-xl border border-border rounded-lg p-1">
          <button
            onClick={handleComment}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-secondary hover:text-text hover:bg-bg-alt rounded transition-colors"
            title="Add comment"
          >
            <MessageSquare className="w-4 h-4" />
            <span>Comment</span>
          </button>
          <div className="w-px h-5 bg-border" />
          <button
            onClick={handleHighlight}
            className="p-1.5 text-text-secondary hover:text-primary hover:bg-primary/10 rounded transition-colors"
            title="Highlight"
          >
            <Highlighter className="w-4 h-4" />
          </button>
          <button
            onClick={handleDeletion}
            className="p-1.5 text-text-secondary hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
            title="Mark for deletion"
          >
            <Strikethrough className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-border" />
          <button
            onClick={onClose}
            className="p-1.5 text-text-muted hover:text-text-secondary rounded transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="bg-bg-card shadow-xl border border-border rounded-lg p-2 w-[300px]">
          <div className="flex items-start gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
            <p className="text-xs text-text-muted truncate flex-1">
              &ldquo;{selection.text.slice(0, 40)}{selection.text.length > 40 ? '...' : ''}&rdquo;
            </p>
          </div>
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add your comment..."
            className="w-full h-16 px-2 py-1.5 text-sm bg-bg-alt border border-border rounded resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 text-text placeholder:text-text-muted"
          />
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => {
                setMode('buttons');
                setComment('');
              }}
              className="text-xs text-text-muted hover:text-text-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitComment}
              disabled={!comment.trim()}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary text-white rounded hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-3 h-3" />
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
